import { API, PolicyWorksheetViewDTO } from "@swivl-mono/api-helper";
import { FeatureFlagKey, GlobalSearchValueMatchInterface, GroupInterface, LocationInterface, OmniDashboard } from "@swivl-mono/types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { isFeatureEnabled } from "../Helpers/FeatureFlags/isFeatureEnabled";
import { usePropertiesGetLocationIds } from "../Hooks/usePropertiesGetLocationIds";
import LocationsModel from "../Models/Locations/Locations.Model";
import { Member } from "../Models/Member/Member.Current";
import { DETAILS_V3_SEARCH_ENTRIES } from "./GlobalSearch.DetailsV3Index";
import { buildResultHref } from "./GlobalSearch.Links";
import { matchEntries } from "./GlobalSearch.Match";
import { buildPolicySearchEntries } from "./GlobalSearch.PolicyIndex";
import { buildReportSearchEntries } from "./GlobalSearch.ReportIndex";
import { LocationSearchEntry, SearchCategoryFilter, SearchEntry, SearchMatch, SearchResultCategory } from "./GlobalSearch.Types";
import { buildValueSearchEntries } from "./GlobalSearch.ValueEntries";

function toLocationEntry(location: LocationInterface, groups: GroupInterface[] | undefined): LocationSearchEntry {
  const group = groups?.find((g) => g.id === location.groupId);
  return {
    id: `location:${location.id}`,
    category: SearchResultCategory.LOCATION,
    title: location.name,
    breadcrumb: group?.name,
    locationId: location.id,
    groupId: location.groupId,
    matchText: location.name.toLowerCase()
  };
}

export interface GroupedSearchMatches {
  category: SearchResultCategory;
  matches: SearchMatch[];
}

const CATEGORY_ORDER = [SearchResultCategory.LOCATION, SearchResultCategory.DETAILS_V3, SearchResultCategory.POLICY, SearchResultCategory.REPORT];

export interface UseGlobalSearchResult {
  query: string;
  setQuery: (value: string) => void;
  hasSearched: boolean;
  submit: () => void;
  /** Sets query and runs it immediately — for example chips, not typing. */
  submitQuery: (text: string) => void;
  clear: () => void;
  groupedResults: GroupedSearchMatches[];
  resultCount: number;
  selectResult: (entry: SearchEntry) => void;
  activeCategory: SearchCategoryFilter;
  setActiveCategory: (category: SearchCategoryFilter) => void;
}

// Combines static Details v3 labels, live Policy content, Locations, and Reports.
export function useGlobalSearch(): UseGlobalSearchResult {
  const [query, setQuery] = useState("");
  const [submittedQuery, setSubmittedQuery] = useState("");
  const [policyWorksheets, setPolicyWorksheets] = useState<PolicyWorksheetViewDTO[]>([]);
  const [dashboards, setDashboards] = useState<OmniDashboard[]>([]);
  const [valueMatches, setValueMatches] = useState<GlobalSearchValueMatchInterface[]>([]);
  const [activeCategory, setActiveCategory] = useState<SearchCategoryFilter>("all");

  const page = usePropertiesGetLocationIds();
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const { locations, groups } = LocationsModel.useState();

  const policyBuilderEnabled = isFeatureEnabled(FeatureFlagKey.PolicyBuilder);
  const orgId = Member.orgId;

  // Refetches on scope change to keep staff-only visibility accurate.
  useEffect(() => {
    if (!policyBuilderEnabled || !orgId) return;
    let cancelled = false;
    API.PolicyWorksheets.getWorksheets(orgId, page.groupId, page.locationId)
      .then((response) => {
        if (!cancelled) setPolicyWorksheets(response.worksheets);
      })
      .catch(() => {
        // Degrade to Details v3 + Locations only — not worth an error toast.
      });
    return () => {
      cancelled = true;
    };
  }, [policyBuilderEnabled, orgId, page.groupId, page.locationId]);

  // Dashboards aren't scoped by group/location, so this fetches once per modal open.
  useEffect(() => {
    let cancelled = false;
    API.Omni.getDashboards()
      .then((response) => {
        if (!cancelled) setDashboards(response);
      })
      .catch(() => {
        // Degrade to the other categories — not worth an error toast.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Exact-match search across operator-authored values, org-wide. Runs on submit only.
  useEffect(() => {
    if (!submittedQuery || !orgId) {
      setValueMatches([]);
      return;
    }
    let cancelled = false;
    API.GlobalSearch.searchValues(orgId, submittedQuery)
      .then((matches) => {
        if (!cancelled) setValueMatches(matches);
      })
      .catch(() => {
        // Degrade to the label-only results — not worth an error toast.
      });
    return () => {
      cancelled = true;
    };
  }, [orgId, submittedQuery]);

  const isStaff = Member.isSwivlStaff;

  const valueEntries = useMemo(
    () => buildValueSearchEntries(valueMatches, Member.org?.name ?? "", groups ?? [], locations ?? [], isStaff),
    [valueMatches, groups, locations, isStaff]
  );

  const allEntries = useMemo<SearchEntry[]>(() => {
    const detailsEntries = DETAILS_V3_SEARCH_ENTRIES.filter((e) => !e.swivlStaffOnly || isStaff);
    const policyEntries = policyBuilderEnabled ? buildPolicySearchEntries(policyWorksheets) : [];
    const locationEntries = (locations ?? []).map((l) => toLocationEntry(l, groups));
    const reportEntries = buildReportSearchEntries(dashboards);
    return [...locationEntries, ...detailsEntries, ...policyEntries, ...reportEntries, ...valueEntries];
  }, [policyBuilderEnabled, policyWorksheets, dashboards, locations, groups, valueEntries, isStaff]);

  const groupedResults = useMemo<GroupedSearchMatches[]>(() => {
    if (!submittedQuery) return [];
    const matches = matchEntries(allEntries, submittedQuery);
    const categories = activeCategory === "all" ? CATEGORY_ORDER : [activeCategory];
    return categories
      .map((category) => ({ category, matches: matches.filter((m) => m.entry.category === category) }))
      .filter((group) => group.matches.length > 0);
  }, [allEntries, submittedQuery, activeCategory]);

  const resultCount = useMemo(() => groupedResults.reduce((sum, g) => sum + g.matches.length, 0), [groupedResults]);

  const submit = useCallback(() => setSubmittedQuery(query), [query]);

  // Sets both pieces of state directly — avoids a stale-query race.
  const submitQuery = useCallback((text: string) => {
    setQuery(text);
    setSubmittedQuery(text);
  }, []);

  const clear = useCallback(() => {
    setQuery("");
    setSubmittedQuery("");
  }, []);

  const selectResult = useCallback(
    (entry: SearchEntry) => {
      navigate(buildResultHref(entry, page, routerLocation.search));
      clear();
    },
    [page, routerLocation.search, navigate, clear]
  );

  return {
    query,
    setQuery,
    hasSearched: submittedQuery.length > 0,
    submit,
    submitQuery,
    clear,
    groupedResults,
    resultCount,
    selectResult,
    activeCategory,
    setActiveCategory
  };
}
